import apiClient from '../client/axios.config';
import { API_ENDPOINTS } from '../endpoints';

/**
 * Normalizes error messages from API responses.
 */
const extractErrorMessage = (err, fallback = 'Something went wrong. Please try again.') => {
  const data = err?.response?.data || err;

  const fieldErrors = Array.isArray(data?.errors) ? data.errors : null;
  if (fieldErrors && fieldErrors.length > 0) {
    const messages = fieldErrors
      .map((e) => e?.msg || e?.message)
      .filter(Boolean);
    if (messages.length) return messages.join(' ');
  }

  return data?.message || err?.message || fallback;
};

/**
 * inAppNotificationService
 *
 * API service for the customer panel notification bell and notifications page.
 * Talks to sm-api-notificationService at /in-app-notifications via API gateway.
 */
export const inAppNotificationService = {
  /**
   * Fetch paginated in-app notifications for the logged-in customer
   * @param {string|Object} [recipientIdOrParams] - User ID or params object
   * @param {Object} [extraParams] - { isRead?, page?, limit? }
   */
  getNotifications: async (recipientIdOrParams, extraParams = {}) => {
    try {
      const queryParams = typeof recipientIdOrParams === 'string'
        ? { recipientId: recipientIdOrParams, ...extraParams }
        : { ...recipientIdOrParams, ...extraParams };

      const res = await apiClient.get(API_ENDPOINTS.IN_APP_NOTIFICATIONS.LIST, {
        params: queryParams,
      });
      return res.data;
    } catch (err) {
      const message = extractErrorMessage(err, 'Failed to fetch notifications. Please try again.');
      const normalizedError = new Error(message);
      normalizedError.original = err;
      normalizedError.status = err?.response?.status || err?.status;
      throw normalizedError;
    }
  },

  getMyNotifications: async (params = {}) => {
    try {
      const res = await apiClient.get(API_ENDPOINTS.IN_APP_NOTIFICATIONS.LIST, {
        params,
      });
      return res.data;
    } catch (err) {
      const message = extractErrorMessage(err, 'Failed to fetch notifications. Please try again.');
      const normalizedError = new Error(message);
      normalizedError.original = err;
      normalizedError.status = err?.response?.status || err?.status;
      throw normalizedError;
    }
  },

  /**
   * Fetch unread notification count
   * @param {string|Object} [recipientIdOrParams]
   */
  getUnreadCount: async (recipientIdOrParams = {}) => {
    try {
      const params = typeof recipientIdOrParams === 'string'
        ? { recipientId: recipientIdOrParams }
        : recipientIdOrParams;

      const res = await apiClient.get(API_ENDPOINTS.IN_APP_NOTIFICATIONS.COUNT, {
        params,
      });
      return res.data?.data?.count ?? res.data?.count ?? 0;
    } catch (err) {
      const message = extractErrorMessage(err, 'Failed to fetch unread count. Please try again.');
      const normalizedError = new Error(message);
      normalizedError.original = err;
      normalizedError.status = err?.response?.status || err?.status;
      throw normalizedError;
    }
  },

  /**
   * Mark a single notification as read
   * @param {string} id - Notification _id
   */
  markAsRead: async (id) => {
    try {
      const res = await apiClient.patch(API_ENDPOINTS.IN_APP_NOTIFICATIONS.MARK_READ(id));
      return res.data;
    } catch (err) {
      const message = extractErrorMessage(err, 'Failed to mark notification as read. Please try again.');
      const normalizedError = new Error(message);
      normalizedError.original = err;
      normalizedError.status = err?.response?.status || err?.status;
      throw normalizedError;
    }
  },

  /**
   * Mark all notifications as read
   * @param {string|Object} [recipientId]
   */
  markAllAsRead: async (recipientId) => {
    try {
      const body = typeof recipientId === 'string'
        ? { recipientId }
        : (recipientId && typeof recipientId === 'object' ? recipientId : {});

      const res = await apiClient.patch(API_ENDPOINTS.IN_APP_NOTIFICATIONS.MARK_ALL, body);
      return res.data;
    } catch (err) {
      const message = extractErrorMessage(err, 'Failed to mark all notifications as read. Please try again.');
      const normalizedError = new Error(message);
      normalizedError.original = err;
      normalizedError.status = err?.response?.status || err?.status;
      throw normalizedError;
    }
  },

  /**
   * Delete a single notification
   * @param {string} id - Notification _id
   */
  deleteNotification: async (id) => {
    try {
      const res = await apiClient.delete(API_ENDPOINTS.IN_APP_NOTIFICATIONS.DELETE(id));
      return res.data;
    } catch (err) {
      const message = extractErrorMessage(err, 'Failed to delete notification. Please try again.');
      const normalizedError = new Error(message);
      normalizedError.original = err;
      normalizedError.status = err?.response?.status || err?.status;
      throw normalizedError;
    }
  },
};
